import { useState, useEffect } from 'react';
import { X, Settings, Check, Info } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { gdprService } from '@/services/gdprService';
import type { ConsentType } from '@/types/gdpr';

export function CookieConsentBanner() {
  const { user } = useAuthStore();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<Record<ConsentType, boolean>>({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
    third_party: false,
    location: false,
    communications: false
  });

  useEffect(() => {
    if (user && !gdprService.hasConsentDecision(user.id)) {
      setShowBanner(true);
    }
  }, [user]);

  const handleAcceptAll = () => {
    if (!user) return;
    
    const allConsent = Object.keys(consent).reduce((acc, key) => {
      acc[key as ConsentType] = true;
      return acc;
    }, {} as Record<ConsentType, boolean>);
    
    gdprService.updateAllConsent(user.id, allConsent);
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    if (!user) return;
    
    gdprService.updateAllConsent(user.id, consent);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    if (!user) return;
    
    const essentialOnly = Object.keys(consent).reduce((acc, key) => {
      acc[key as ConsentType] = key === 'essential';
      return acc;
    }, {} as Record<ConsentType, boolean>);
    
    gdprService.updateAllConsent(user.id, essentialOnly);
    setShowBanner(false);
  };

  const toggleConsent = (type: ConsentType) => {
    if (type === 'essential') return; // Cannot disable essential cookies
    
    setConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const cookieCategories = gdprService.getCookieCategories();

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto p-4">
        {!showDetails ? (
          // Simple Banner
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                We value your privacy
              </h3>
              <p className="text-sm text-gray-600">
                We use cookies to enhance your experience, analyze site usage, and assist in marketing. 
                You can customize your preferences or accept all cookies.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 min-w-fit">
              <button
                onClick={() => setShowDetails(true)}
                className="btn btn-outline flex items-center text-sm"
              >
                <Settings className="w-4 h-4 mr-2" />
                Customize
              </button>
              <button
                onClick={handleRejectAll}
                className="btn btn-outline text-sm"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="btn btn-primary text-sm"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Detailed Settings
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid gap-4 max-h-60 overflow-y-auto">
              {cookieCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="font-medium text-gray-900">{category.name}</h4>
                        {category.essential && (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                      
                      <details className="text-xs text-gray-500">
                        <summary className="cursor-pointer hover:text-gray-700 flex items-center">
                          <Info className="w-3 h-3 mr-1" />
                          View cookies ({category.cookies.length})
                        </summary>
                        <div className="mt-2 space-y-1">
                          {category.cookies.map((cookie, index) => (
                            <div key={index} className="pl-4 border-l-2 border-gray-100">
                              <div className="font-medium">{cookie.name}</div>
                              <div>Purpose: {cookie.purpose}</div>
                              <div>Duration: {cookie.duration}</div>
                              <div>Provider: {cookie.provider}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                    
                    <div className="ml-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consent[category.id]}
                          onChange={() => toggleConsent(category.id)}
                          disabled={category.essential}
                          className="sr-only"
                        />
                        <div className={`
                          w-12 h-6 rounded-full transition-colors duration-200 ease-in-out
                          ${consent[category.id] ? 'bg-primary-600' : 'bg-gray-300'}
                          ${category.essential ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}>
                          <div className={`
                            w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out
                            ${consent[category.id] ? 'translate-x-6' : 'translate-x-0.5'}
                            mt-0.5
                          `} />
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                onClick={handleRejectAll}
                className="btn btn-outline"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptSelected}
                className="btn btn-primary flex items-center"
              >
                <Check className="w-4 h-4 mr-2" />
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}